<script lang="ts" setup>
import { type PropType } from "vue";
import ButtonSecondary from "~/components/ButtonSecondary.vue";
import PaymentCreditCard from "~/components/checkout/PaymentCreditCard.vue";
import H3Heading from "~/components/H3Heading.vue";
import ButtonPrimary from "~/components/ButtonPrimary.vue";
import PaymentACH from "~/components/checkout/PaymentACH.vue";
import { useHelper } from "~/composables/helper";
import axios from "axios";
import type SavedPayment from "~/types/SavedPayment";

// Properties
const props = defineProps({
    user: {
        type: Object,
    },
    paymentMethod: {
        type: Object as PropType<SavedPayment>,
    },
});

// Data
const config = useRuntimeConfig();
const emit = defineEmits(["update:user"]);
const showRemoveModalBox = ref(false);
const showEditModalBox = ref(false);
const errorMsg = ref("");
const refPaymentCC = ref();
const refPaymentACH = ref();
const ACH = "ach";
const CC = "cc";

const { getApiError, formatCardExpire, upperCaseFirstLetter } = useHelper();

// Functions
async function handleRemove() {
    try {
        const form = { id: props.paymentMethod?.id };
        await axios.post(config.public.STATAMIC_API + `/user/${props.user?.id}/remove-payment-method`, form);

        errorMsg.value = "<div>Remove successfully!</div>";

        setTimeout(function () {
            showRemoveModalBox.value = false;
            errorMsg.value = "";

            // Update props user
            const updatedUser = props.user;
            const updatedPayments = [];

            if (!updatedUser) {
                return;
            }

            for (let i = 0; i < updatedUser?.payment_methods.length; i++) {
                if (updatedUser?.payment_methods[i].id != form.id) {
                    updatedPayments.push(updatedUser?.payment_methods[i]);
                }
            }

            updatedUser.payment_methods = updatedPayments;
            emit("update:user", updatedUser);
        });
    } catch (e: any) {
        errorMsg.value = getApiError(e);
    }
}

async function handleEdit() {
    try {
        const form =
            props.paymentMethod?.payment.paymentType === ACH ? refPaymentACH.value.form : refPaymentCC.value.form;

        await axios.post(config.public.STATAMIC_API + `/user/${props.user?.id}/edit-payment-method`, form);

        errorMsg.value = "<div>Updated successfully!</div>";

        setTimeout(function () {
            showEditModalBox.value = false;
            errorMsg.value = "";

            // When payment is null, it doesn't pass back the data to the parent. Reload for now.
            if (!props.user?.payment_methods) {
                window.location.href = "/account/details#payment";
                window.location.reload();
            }

            // Update props user
            const updatedUser = props.user;
            if (!updatedUser) {
                return;
            }

            for (let i = 0; i < updatedUser?.payment_methods.length; i++) {
                if (updatedUser?.payment_methods[i].id !== form.id) {
                    continue;
                }

                updatedUser.payment_methods[i].payment = form;
            }
            emit("update:user", updatedUser);
        }, 2000);
    } catch (e: any) {
        errorMsg.value = getApiError(e);
        console.log(e);
    }
}
</script>

<template>
    <div>
        <div class="flex">
            <div class="mr-3 w-8 flex-none">
                <img
                    v-if="String(paymentMethod.payment.paymentType).includes(ACH)"
                    src="/images/checkout/ACH.svg"
                    class="w-full"
                />
                <img
                    v-else-if="String(paymentMethod.payment.cardType).includes('american')"
                    src="/images/checkout/AXP.svg"
                    class="w-full"
                />
                <img
                    v-else-if="String(paymentMethod.payment.cardType).includes('master')"
                    src="/images/checkout/mc_symbol.svg"
                    class="w-full"
                />
                <img
                    v-else-if="String(paymentMethod.payment.cardType).includes('visa')"
                    src="/images/checkout/Visa.svg"
                    class="mt-2 w-full"
                />
                <img
                    v-else-if="String(paymentMethod.payment.cardType).includes('discover')"
                    src="/images/checkout/DGN.png"
                    class="w-full"
                />
            </div>
            <div class="flex-none font-bold md:flex">
                <div class="flex-1">
                    <span v-if="String(paymentMethod.payment.paymentType).includes(ACH)">
                        Bank ending in
                        {{
                            paymentMethod.payment.accountNumber.substring(
                                paymentMethod.payment.accountNumber.length - 4,
                            )
                        }}
                    </span>
                    <span v-else>
                        {{ upperCaseFirstLetter(paymentMethod.payment.cardType) }} ending in
                        {{ paymentMethod.payment.cardNumber.substring(paymentMethod.payment.cardNumber.length - 4) }}
                    </span>

                    <div class="text-sm font-normal">
                        <span v-if="String(paymentMethod.payment.paymentType).includes(ACH)">
                            {{ upperCaseFirstLetter(paymentMethod.payment.accountType) }}
                        </span>
                        <span v-else>
                            <span v-if="paymentMethod.payment.setAsDefault">Preferred Method |</span>
                            Expires
                            {{ formatCardExpire(paymentMethod.payment.expMonth, paymentMethod.payment.expYear) }}
                        </span>
                    </div>
                </div>
                <div class="flex-none font-normal text-gray-300 md:pl-8">
                    <span class="text-abc-blue cursor-pointer" @click="showRemoveModalBox = true">Remove</span>
                    <span class="mx-2">|</span>
                    <span class="text-abc-blue cursor-pointer" @click="showEditModalBox = true">Edit</span>
                </div>
            </div>
        </div>

        <ModalBox v-model:showModalBox="showRemoveModalBox" width="w-11/12 md:w-96">
            <H3Heading class="mb-4 text-center">Remove payment method</H3Heading>
            <p class="text-center">Are you sure you would like to permanently remove this payment method?</p>
            <div class="mt-10 text-center">
                <ButtonPrimary class="w-full" @click="handleRemove">Remove</ButtonPrimary>
                <ButtonSecondary class="mt-4 w-full" @click="showRemoveModalBox = false">Cancel</ButtonSecondary>
            </div>
        </ModalBox>

        <ModalBox v-model:showModalBox="showEditModalBox" width="w-11/12 lg:w-3/5">
            <form @submit.prevent="handleEdit">
                <PaymentCreditCard
                    v-if="paymentMethod.payment.paymentType == CC"
                    ref="refPaymentCC"
                    :key="CC + paymentMethod.id"
                    :is-edit="true"
                    :initial-data="paymentMethod"
                    :show-checkbox-default-payment="true"
                />
                <PaymentACH
                    v-else-if="paymentMethod.payment.paymentType == ACH"
                    ref="refPaymentACH"
                    :key="ACH + paymentMethod.id"
                    :is-edit="true"
                    :initial-data="paymentMethod"
                />

                <div class="mt-4 text-center md:mt-8 md:text-left">
                    <div v-if="errorMsg" class="pb-8" v-html="errorMsg"></div>
                    <ButtonSecondary type="submit" class="w-full md:inline-block md:w-auto md:px-10"
                        >Save Changes
                    </ButtonSecondary>
                    <span
                        class="text-abc-blue mt-4 inline-block cursor-pointer md:mt-0 md:ml-8"
                        @click="showEditModalBox = false"
                        >Cancel</span
                    >
                </div>
            </form>
        </ModalBox>
    </div>
</template>

<style scoped></style>
